import type { CrmUser } from "@/lib/api_client";
import { toCsv } from "@/lib/utils/csv";
import { formatCurrency, getTierLabel } from "./formatters";

export type CustomerFilters = {
  gender: string;
  minAge: string;
  maxAge: string;
  wearables: string;
  minVisits: string;
  maxVisits: string;
  tiers: string[];
  createdFrom: string;
  createdTo: string;
  active: string;
};

export const emptyCustomerFilters: CustomerFilters = {
  gender: "",
  minAge: "",
  maxAge: "",
  wearables: "",
  minVisits: "",
  maxVisits: "",
  tiers: [],
  createdFrom: "",
  createdTo: "",
  active: "",
};

export const customerDefinitions = {
  wearables:
    "Ja når en Garmin-tilkobling finnes, eller kunden har synkroniserte data fra Apple Health eller Health Connect. Tilgang i telefonens helseapp kan senere ha blitt trukket tilbake.",
  visits:
    "Gjennomførte labbesøk gjennom hele kundeforholdet: venter på resultater, resultater mottatt eller vurdering fullført. Planlagte og selvrapporterte besøk er ikke inkludert.",
  subscription:
    "Nåværende abonnementstype. For tidligere abonnenter vises lagret type fra siste reelle abonnement. Mislykkede betalingsforsøk regnes ikke som abonnement. Ingen abonnement betyr at kunden aldri har hatt et reelt abonnement.",
};

export function genderLabel(gender: string | null | undefined): string {
  return gender === "male" ? "Mann" : gender === "female" ? "Kvinne" : "Ukjent";
}

export function subscriptionLabel(tier: string | null | undefined): string {
  return tier === "none"
    ? "Ingen abonnement"
    : !tier || tier === "unknown"
      ? "Ukjent"
      : getTierLabel(tier);
}

export function validateCustomerFilters(f: CustomerFilters): string | null {
  // The view's age is PostgreSQL int4, not a JavaScript-safe integer.
  if (
    [f.minAge, f.maxAge].some(
      (value) => value !== "" && Number(value) > 2_147_483_647,
    )
  )
    return "Alder: maksimum tillatt verdi er 2147483647.";
  for (const [min, max, label] of [
    [f.minAge, f.maxAge, "Alder"],
    [f.minVisits, f.maxVisits, "Labbesøk"],
  ]) {
    if (
      [min, max].some(
        (v) =>
          v !== "" && (!/^\d+$/.test(v!) || !Number.isSafeInteger(Number(v))),
      )
    )
      return `${label}: bruk hele, positive tall eller 0.`;
    if (min && max && Number(min) > Number(max))
      return `${label}: minimum må være mindre enn eller lik maksimum.`;
  }
  if (f.createdFrom && f.createdTo && f.createdFrom > f.createdTo)
    return "Opprettet: fra-dato må være før eller lik til-dato.";
  return null;
}

/** The API and export share these exact filters. Date bounds include the whole UTC day. */
export function customerFilterQuery(f: CustomerFilters) {
  const range = (min: string, max: string) => {
    const bounds = [
      ...(min ? [`gte:${min}`] : []),
      ...(max ? [`lte:${max}`] : []),
    ];
    return bounds.length > 1 ? bounds : bounds[0];
  };
  const dates: string[] = [];
  if (f.createdFrom) dates.push(`gte:${f.createdFrom}T00:00:00Z`);
  if (f.createdTo) {
    const end = new Date(`${f.createdTo}T00:00:00Z`);
    end.setUTCDate(end.getUTCDate() + 1);
    dates.push(`lt:${end.toISOString()}`);
  }
  return {
    "filter[gender]":
      f.gender === "unknown" ? "is_nil:true" : f.gender || undefined,
    "filter[age]": range(f.minAge, f.maxAge),
    "filter[wearablesConnected]": f.wearables || undefined,
    "filter[usedVisits]": range(f.minVisits, f.maxVisits),
    "filter[subscriptionTier]": f.tiers.length
      ? `in:${f.tiers.join(",")}`
      : undefined,
    "filter[accountCreated]": dates.length > 1 ? dates : dates[0],
    "filter[hasActiveSubscription]": f.active || undefined,
  };
}

export type CustomerSortKey =
  | "fullName"
  | "email"
  | "subscriptionTier"
  | "usedVisits"
  | "totalAmountSpent"
  | "accountCreated"
  | "gender"
  | "age"
  | "wearablesConnected";
export type CustomerSortDir = "asc" | "desc";

export function sortCustomers(
  users: CrmUser[],
  key: CustomerSortKey,
  dir: CustomerSortDir,
): CrmUser[] {
  return [...users].sort((a, b) => {
    const av = a[key],
      bv = b[key];
    // Match Postgres: ASC NULLS LAST, DESC NULLS FIRST.
    let cmp: number;
    if (av == null || bv == null)
      cmp = av == null && bv == null ? 0 : av == null ? 1 : -1;
    else if (key === "totalAmountSpent") cmp = Number(av) - Number(bv);
    else if (typeof av === "number" || typeof av === "boolean")
      cmp = Number(av) - Number(bv);
    else cmp = String(av).localeCompare(String(bv), "nb");
    return (
      (dir === "desc" ? -cmp : cmp) ||
      (a.authUserId ?? "").localeCompare(b.authUserId ?? "")
    );
  });
}

export function customerFilterSummary(
  f: CustomerFilters,
  search: string,
  order: string,
): string {
  const parts = [
    search.trim().length >= 2 ? `Søk: ${search.trim()}` : null,
    f.gender ? `Kjønn: ${genderLabel(f.gender)}` : null,
    f.minAge || f.maxAge
      ? `Alder: ${f.minAge || "0"}–${f.maxAge || "ubegrenset"}`
      : null,
    f.wearables ? `Wearables: ${f.wearables === "true" ? "Ja" : "Nei"}` : null,
    f.minVisits || f.maxVisits
      ? `Labbesøk: ${f.minVisits || "0"}–${f.maxVisits || "ubegrenset"}`
      : null,
    f.tiers.length
      ? `Abonnement: ${f.tiers.map(subscriptionLabel).join(", ")}`
      : null,
    f.createdFrom || f.createdTo
      ? `Opprettet (UTC): ${f.createdFrom || "ubegrenset"}–${f.createdTo || "ubegrenset"}`
      : null,
    f.active
      ? `Aktivt abonnement: ${f.active === "true" ? "Ja" : "Nei"}`
      : null,
  ].filter(Boolean);
  return `${parts.length ? parts.join("; ") : "Ingen filtre"}; Sortering: ${order}`;
}

export function customerCsv(
  users: CrmUser[],
  filters: CustomerFilters,
  search: string,
  order: string,
  exportedAt: string,
): string {
  const summary = customerFilterSummary(filters, search, order);
  return toCsv(
    [
      "Navn",
      "E-post",
      "Abonnement",
      "Labbesøk",
      "Totalt brukt",
      "Opprettet (UTC)",
      "Aktiv",
      "Wearables tilkoblet / data tilgjengelig",
      "Kjønn",
      "Alder",
      "Valgte filtre",
      "Eksportert",
      "Wearables-definisjon",
      "Labbesøk-definisjon",
      "Abonnement-definisjon",
    ],
    users.map((u) => [
      u.fullName ||
        [u.givenName, u.familyName].filter(Boolean).join(" ") ||
        "Ukjent",
      u.email,
      subscriptionLabel(u.subscriptionTier),
      u.usedVisits ?? 0,
      formatCurrency(Number(u.totalAmountSpent ?? 0) / 100),
      u.accountCreated ?? "",
      u.hasActiveSubscription ? "Ja" : "Nei",
      u.wearablesConnected ? "Ja" : "Nei",
      genderLabel(u.gender),
      u.age ?? "Ukjent",
      summary,
      exportedAt,
      customerDefinitions.wearables,
      customerDefinitions.visits,
      customerDefinitions.subscription,
    ]),
    ";",
    { neutralizeFormulas: true },
  );
}
